/**
 * The holes a hand-entered slope table covers. A generated table reads its
 * range off the evaluation; a manual one has nothing to read, so the range is
 * picked here instead.
 */
export const FIRST_HOLE = 1
export const LAST_HOLE = 18

export type HoleRange = {
  startHole: number
  endHole: number
}

export type HoleRangePreset = 'full' | 'front9' | 'back9' | 'custom'

export const DEFAULT_HOLE_RANGE: HoleRange = {
  startHole: FIRST_HOLE,
  endHole: LAST_HOLE,
}

export const HOLE_RANGE_PRESETS: {
  value: HoleRangePreset
  label: string
  hint: string
  range?: HoleRange
}[] = [
  {
    value: 'full',
    label: 'Full round',
    hint: 'Holes 1-18',
    range: { startHole: 1, endHole: 18 },
  },
  {
    value: 'front9',
    label: 'Front nine',
    hint: 'Holes 1-9',
    range: { startHole: 1, endHole: 9 },
  },
  {
    value: 'back9',
    label: 'Back nine',
    hint: 'Holes 10-18',
    range: { startHole: 10, endHole: 18 },
  },
  {
    value: 'custom',
    label: 'Custom range',
    hint: 'Pick a first and a last hole',
  },
]

/** Every hole that can be picked, in the order they are played. */
export const ALL_HOLES = Array.from(
  { length: LAST_HOLE - FIRST_HOLE + 1 },
  (_, index) => FIRST_HOLE + index
)

/**
 * Which preset a saved range matches, so reopening a table shows the choice
 * that was made instead of always falling back to Custom.
 */
export const holeRangePreset = ({
  startHole,
  endHole,
}: HoleRange): HoleRangePreset =>
  HOLE_RANGE_PRESETS.find(
    (preset) =>
      preset.range?.startHole === startHole && preset.range?.endHole === endHole
  )?.value ?? 'custom'

/**
 * The range as the header spells it. Tables saved before the range could be
 * picked have none, and those say nothing rather than claim a full round.
 */
export const formatHoleRange = (startHole?: number, endHole?: number) =>
  startHole && endHole && endHole >= startHole
    ? `${startHole}-${endHole}`
    : undefined

export const holeCount = ({ startHole, endHole }: HoleRange) =>
  endHole - startHole + 1

/**
 * What the form field reads once the range is picked. A preset is named, since
 * that is what was chosen; anything else only has its holes to go by.
 */
export const holeRangeLabel = (range: HoleRange) => {
  const preset = HOLE_RANGE_PRESETS.find(
    ({ value }) => value === holeRangePreset(range)
  )
  const span = formatHoleRange(range.startHole, range.endHole)
  return preset?.range ? `${preset.label} · ${span}` : `Holes ${span}`
}
